import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SEO from "../lib/seo";
import { BACKEND } from "../lib/api";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import SectionHead from "../components/SectionHead";
import SpotlightCard from "../components/SpotlightCard";
import TiltCard from "../components/TiltCard";
import AnimatedCounter from "../components/AnimatedCounter";
import CTASection, { GhostButton, PrimaryButton } from "../components/CTASection";
import { FUTURE_THEMES } from "../data/site";

const DOMAINS = [
  "Enterprise IT infrastructure & system integration", "Data centre design (Tier I–IV) & facilities", "Servers, storage, computing & digital workplace",
  "Backup, disaster recovery & business continuity", "Virtualization, containers & software-defined infrastructure", "Networking, wireless & network management",
  "Structured cabling & passive infrastructure", "Cybersecurity & information security", "Security operations (SOC), testing & compliance",
  "Cloud computing & managed cloud services", "Software licensing & enterprise applications", "Software development & digital platforms",
  "DevOps, DevSecOps, AI & automation", "IoT, edge & emerging technologies", "Data analytics, engineering & business intelligence",
  "ERP, CRM & business applications", "Managed IT services & outsourcing (AMC/RIM)", "IT consulting & digital transformation",
  "Telephony, VoIP & unified communications", "Audio-visual & collaboration solutions", "CCTV, access control & electronic security",
  "UPS, power & electrical infrastructure", "IT hardware, software & technology trading", "Import & export of technology products",
  "IT asset lifecycle & e-waste services", "Website, hosting & digital marketing", "IT-enabled & business process services",
  "Technology training & professional services", "Technology research, development & innovation", "Technology partnerships & IT manpower support",
];

const STATS = [
  { to: 30, suffix: "", label: "CAPABILITY DOMAINS" },
  { to: 7, suffix: "", label: "SOLUTION PRACTICES" },
  { to: 10, suffix: "+", label: "TECHNOLOGY SPECIALISTS" },
  { to: 10, suffix: "+", label: "CUSTOMERS SERVED" },
];

export default function About() {
  const [founder, setFounder] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    fetch(`${BACKEND}/api/settings`)
      .then((r) => r.json())
      .then((s) => setFounder(s))
      .catch(() => {});
  }, []);
  const hasFounder = !!(founder && founder.founderName);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "About Aviokan Technologies",
    description: "Aviokan Technologies Private Limited — IT system integrator, technology solutions provider, digital engineering company and IT consulting partner.",
  };
  return (
    <div data-testid="about-page" className="bg-[#0B0C10]">
      <SEO title="About Aviokan" description="Aviokan Technologies Private Limited is an IT system integrator, technology solutions provider, digital engineering company and IT consulting partner based in Noida, India." path="/about" jsonLd={jsonLd} />

      <PageHero
        eyebrow="ABOUT AVIOKAN"
        title={"Technology built\naround your business."}
        desc="Aviokan Technologies Private Limited is an emerging technology company operating as an IT System Integrator, Technology Solutions Provider, Digital Engineering Company and IT Consulting Partner."
        breadcrumb={[{ label: "Home", to: "/" }, { label: "About" }]}
        image="/images/home/hero-operations.jpg"
      >
        <GhostButton to="/about/mission-vision">Mission & Vision</GhostButton>
      </PageHero>

      {/* Signal stats strip */}
      <section className="bg-[#0B0C10] border-b border-white/10" data-testid="about-stats">
        <div className="mx-auto max-w-[1320px] px-5 md:px-10 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          {STATS.map((s) => (
            <div key={s.label} className="bg-[#0B0C10] px-6 py-8">
              <AnimatedCounter to={s.to} suffix={s.suffix} className="font-display font-black text-4xl md:text-5xl text-white" />
              <p className="text-[10px] font-tech tracking-[0.28em] text-cy mt-3">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Who we are */}
      <section className="bg-[#0B0C10] py-24" data-testid="about-who-we-are">
        <div className="mx-auto max-w-[1320px] px-5 md:px-10 grid lg:grid-cols-[1.1fr_0.9fr] gap-14 items-center">
          <Reveal>
            <p className="text-[11px] font-tech tracking-[0.3em] text-cy mb-6">WHO WE ARE</p>
            <h2 className="font-display font-black text-3xl md:text-5xl text-white tracking-tight leading-[1.05]">A technology partner, <span className="text-cy">not a product reseller.</span></h2>
            <div className="mt-8 space-y-5 text-base text-white/65 leading-relaxed">
              <p>Aviokan helps organizations design, integrate, secure, modernize and manage their technology environment. From infrastructure and cybersecurity to cloud, collaboration and digital engineering, we bring multiple technology domains together into integrated business-ready solutions.</p>
              <p>Our approach is engineering-led. We take responsibility for the whole arc — understanding the requirement, designing the architecture, integrating multiple technologies, deploying the solution, providing managed services and continuously optimizing the environment.</p>
              <p>We serve SMB, mid-market, enterprise, institutional and government customers — always with the same discipline: technology recommendations must connect with actual business requirements.</p>
            </div>
            <div className="mt-10"><PrimaryButton to="/contact">Talk to an Expert <ArrowRight size={15} /></PrimaryButton></div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative group">
              <div className="absolute -inset-px bg-gradient-to-br from-cy/50 via-transparent to-ember/40 opacity-60 group-hover:opacity-100 transition-opacity duration-700" aria-hidden="true" />
              <div className="relative border border-white/10 bg-[#14151A] p-2.5">
                <img src="/images/about/culture.jpg" alt="Aviokan technology team collaborating in a modern office" loading="lazy" className="w-full aspect-[4/5] object-cover" />
                <div className="absolute inset-2.5 ring-1 ring-inset ring-cy/30 pointer-events-none" aria-hidden="true" />
                <p className="absolute bottom-5 left-5 text-[10px] font-tech tracking-[0.3em] text-white/70 bg-[#0B0C10]/80 backdrop-blur px-3 py-1.5 border border-white/10">AVIOKAN · ENGINEERING CULTURE</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What we do — 30-domain circuit grid */}
      <section className="bg-[#14151A] py-24 border-y border-white/10 overflow-hidden" data-testid="about-what-we-do">
        <div className="grain" aria-hidden="true" />
        <div className="mx-auto max-w-[1320px] px-5 md:px-10 relative">
          <SectionHead eyebrow="What we do" title="Across the full technology estate." dark />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-14">
            {DOMAINS.map((d, i) => (
              <Reveal key={d} delay={Math.min((i % 6) * 0.04, 0.2)}>
                <SpotlightCard variant="dark" className="h-full" testId={`domain-${i + 1}`}>
                  <div className="flex items-center gap-4 h-full border border-white/10 bg-[#0B0C10]/80 px-5 py-4 hover:border-cy/40 transition-colors duration-300">
                    <span className="font-tech text-[10px] text-ember/80 tabular-nums shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span className="h-1 w-1 rotate-45 bg-cy shrink-0" aria-hidden="true" />
                    <span className="text-sm text-white/70 font-medium">{d}</span>
                  </div>
                </SpotlightCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy — ghost-numeral tilt cards */}
      <section className="bg-[#0B0C10] py-24" data-testid="about-philosophy">
        <div className="mx-auto max-w-[1320px] px-5 md:px-10">
          <SectionHead eyebrow="How we think" title="Three beliefs behind every engagement." dark />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-14">
            {[
              ["Technology philosophy", "Technology exists to serve business outcomes. We recommend, integrate and manage technology that measurably improves resilience, security and productivity — and we say no to complexity that serves no purpose."],
              ["System integration approach", "Integration is our core craft: making compute, network, security, cloud and applications from multiple ecosystems behave as one managed system with shared identity, policy and monitoring."],
              ["Customer-centric focus", "Every engagement starts with discovery — your goals, users, constraints and compliance context. Solutions are designed around them, documented, and supported across the lifecycle."],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.08}>
                <TiltCard className="h-full">
                  <div className="relative h-full border border-white/10 bg-[#14151A] p-8 pt-14 overflow-hidden hover:border-cy/40 transition-colors duration-500" data-testid={`philosophy-${i + 1}`}>
                    <span className="absolute -top-3 right-4 font-tech font-black text-8xl leading-none text-transparent select-none" style={{ WebkitTextStroke: "1px rgba(0,137,161,0.4)" }} aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                    <span className="absolute top-0 left-0 h-[3px] w-16 bg-ember" aria-hidden="true" />
                    <h3 className="font-display font-bold text-xl text-white relative">{t}</h3>
                    <p className="text-sm text-white/60 leading-relaxed mt-5 relative">{d}</p>
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Future themes — retro-future grid */}
      <section className="bg-[#14151A] py-24 border-y border-white/10 overflow-hidden" data-testid="future-themes">
        <div className="grain" aria-hidden="true" />
        <div className="mx-auto max-w-[1320px] px-5 md:px-10 relative">
          <SectionHead eyebrow="Where we are heading" title="Six themes shape every design and roadmap we deliver." dark lead="Technology choices made today should still be right in five years. These are the directions our engineering roadmap is built around." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
            {FUTURE_THEMES.map((t, i) => (
              <Reveal key={t.title} delay={(i % 3) * 0.06}>
                <div className="relative h-full border border-white/10 bg-[#0B0C10]/80 p-7 hover:border-cy/50 transition-colors duration-300 group" data-testid={`future-theme-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-tech text-xs text-ember">{String(i + 1).padStart(2, "0")}</span>
                    <span className="h-1.5 w-1.5 rotate-45 bg-cy/60 group-hover:bg-ember transition-colors duration-300" aria-hidden="true" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-white mt-4">{t.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed mt-2.5">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Founder — classified dossier */}
      <section className="bg-[#0B0C10] py-24" data-testid="founder-section">
        <div className="mx-auto max-w-[1320px] px-5 md:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-center">
          <Reveal>
            <div
              className="relative border border-white/10 bg-[#14151A] p-10 md:p-14 flex flex-col items-center text-center max-w-sm mx-auto lg:mx-0 overflow-hidden"
              data-testid="founder-profile"
              style={{ backgroundImage: "repeating-linear-gradient(0deg, rgba(255,255,255,0.025) 0px, rgba(255,255,255,0.025) 1px, transparent 1px, transparent 4px)" }}
            >
              <div className="absolute -top-20 left-1/2 -translate-x-1/2 h-56 w-56 rounded-full bg-cy/15 blur-[80px] pointer-events-none" aria-hidden="true" />
              <span className="absolute top-0 right-0 h-10 w-10 border-t border-r border-cy/50" aria-hidden="true" />
              <span className="absolute bottom-0 left-0 h-10 w-10 border-b border-l border-cy/50" aria-hidden="true" />
              <div className="relative mt-2">
                <motion.span
                  className="absolute -inset-3 rounded-full border border-dashed border-cy/25"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
                  aria-hidden="true"
                />
                {hasFounder && founder.founderPhoto ? (
                  <img src={founder.founderPhoto} alt={founder.founderName} className="relative h-28 w-28 rounded-full border border-cy/40 object-cover" />
                ) : (
                  <span className="relative grid place-items-center h-28 w-28 rounded-full bg-[#0B0C10] border border-cy/40 text-cy font-display font-black text-4xl shadow-[0_0_50px_rgba(0,194,209,0.18)]" aria-hidden="true">A</span>
                )}
              </div>
              <h2 className="font-display font-black text-2xl text-white mt-8">{hasFounder ? founder.founderName : "Founder & Managing Director"}</h2>
              {hasFounder && founder.founderTitle ? <p className="text-sm font-tech tracking-[0.2em] text-cy mt-2">{founder.founderTitle}</p> : null}
              {hasFounder && founder.founderBio ? <p className="text-xs text-white/60 mt-4 leading-relaxed">{founder.founderBio}</p> : null}
              {!hasFounder && <span className="h-1.5 w-1.5 rotate-45 bg-ember mt-6" aria-hidden="true" />}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <SectionHead eyebrow="Leadership" title="Led by engineers, run like an integrator." dark />
            <p className="mt-8 text-base text-white/65 leading-relaxed">
              Aviokan is built around a simple operating idea: technical decisions should be made by people who will also implement and support them. Leadership stays involved in architecture reviews, delivery quality and customer outcomes.
            </p>
            <p className="mt-4 text-sm text-white/55 leading-relaxed">
              Our engineering team is led by IITians &amp; NITians — graduates of the Indian Institutes of Technology and the National Institutes of Technology — bringing design rigour and first-principles problem solving to every engagement, backed by a team of 10+ technology specialists and solutions delivered for 10+ customers across domains.
            </p>
            <p className="mt-4 text-sm text-white/55 leading-relaxed">
              A detailed leadership profile, governance structure and organizational information can be shared on request as part of vendor-empanelment and due-diligence processes.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Explore the About section */}
      <section className="bg-[#14151A] py-20 border-t border-white/10" data-testid="about-links">
        <div className="mx-auto max-w-[1320px] px-5 md:px-10">
          <SectionHead eyebrow="Continue" title="Go deeper." dark />
          <div className="grid sm:grid-cols-3 gap-5 mt-12">
            {[
              ["Mission & Vision", "What drives Aviokan.", "/about/mission-vision"],
              ["Our Approach", "Discover → Design → Integrate → Deploy → Secure → Manage.", "/about/approach"],
              ["Careers", "Build your career in enterprise technology.", "/about/careers"],
            ].map(([t, d, to], i) => (
              <Reveal key={t} delay={i * 0.07}>
                <Link to={to} className="group block h-full border border-white/10 bg-[#0B0C10]/80 p-8 hover:border-cy/50 transition-colors duration-300">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-bold text-xl text-white group-hover:text-cy transition-colors">{t}</h3>
                    <ArrowUpRight size={18} className="text-white/30 group-hover:text-ember group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                  </div>
                  <p className="text-sm text-white/55 mt-3">{d}</p>
                  <span className="block h-px w-full bg-gradient-to-r from-cy/40 to-transparent mt-6 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" aria-hidden="true" />
                  <span className="inline-flex items-center gap-2 text-cy text-xs font-bold mt-5">Explore <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}